/**
 * "Install app": Chrome/Edge/Android fire `beforeinstallprompt`, which we keep to show our own
 * button; iOS Safari has no prompt, so it gets a hint (Share → Add to Home Screen). Listening
 * starts at module load (main.tsx imports this) because the event can fire before React renders.
 */
type InstallEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};

let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferred = event as InstallEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
  });
}

export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const canInstall = () => deferred !== null;

/** Opens the browser's install dialog; true when the user installed. */
export async function install(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  deferred = null;
  notify();
  await event.prompt();
  return (await event.userChoice).outcome === "accepted";
}

/** Running as an installed app (home screen), not in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone/iPad Safari, where installing goes through the Share menu. */
export function needsIosHint(userAgent = navigator.userAgent): boolean {
  const ios = /iphone|ipad|ipod/i.test(userAgent);
  return ios && !/crios|fxios|edgios/i.test(userAgent) && !isStandalone();
}
